import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useAuth } from './AuthProvider';
import { fetchGeneralSettings, fetchCurrencies } from '../services/Http/Api';
import {
    formatCurrency as formatCurrencyUtil,
    formatCurrencyCompact,
    formatCurrencyRange,
    getCurrencySymbol,
    DEFAULT_CURRENCY,
} from '../utils/currencyFormatter';

const CurrencyContext = createContext();

export const useCurrency = () => useContext(CurrencyContext);

const CurrencyProvider = ({ children }) => {
    const { isAuthenticated, user } = useAuth();

    // Current currency state
    const [currentCurrency, setCurrentCurrency] = useState(DEFAULT_CURRENCY);
    const [currencyId, setCurrencyId] = useState(null);
    const [exchangeRate, setExchangeRate] = useState(1);

    // Available currencies list
    const [currencies, setCurrencies] = useState([]);
    const [currenciesLoading, setCurrenciesLoading] = useState(false);

    // Organization's default currency
    const [organizationCurrency, setOrganizationCurrency] = useState(DEFAULT_CURRENCY);
    const NULL_DISPLAY = '—';

    const hasNumericValue = useCallback((value) => {
        if (value === null || value === undefined) return false;
        if (typeof value === 'string' && value.trim() === '') return false;
        const parsed = Number(value);
        return Number.isFinite(parsed);
    }, []);

    const normalizeCurrencyCode = useCallback((code) => {
        if (code === null || code === undefined) return null;
        const value = String(code).trim();
        if (!value) return null;
        return value.toUpperCase();
    }, []);

    /**
     * Load available currencies from API
     */
    const loadCurrencies = useCallback(async () => {
        setCurrenciesLoading(true);
        try {
            const response = await fetchCurrencies();
            if (response?.data) {
                setCurrencies(response.data);
            } else if (Array.isArray(response)) {
                setCurrencies(response);
            }
        } catch (error) {
            console.error('Failed to load currencies:', error);
            // Set default EUR if fetch fails
            setCurrencies([{ id: 1, code: 'EUR', name: 'Euro', symbol: '€' }]);
        } finally {
            setCurrenciesLoading(false);
        }
    }, []);

    /**
     * Load organization settings to get default currency
     */
    const loadOrganizationCurrency = useCallback(async () => {
        if (!isAuthenticated || user?.is_system_admin) {
            return;
        }

        try {
            const settings = await fetchGeneralSettings();
            if (settings?.currency) {
                const currencyCode = settings.currency.code || settings.currency.key || settings.currency;
                setOrganizationCurrency(currencyCode);
                setCurrentCurrency(currencyCode);
                setCurrencyId(settings.currency.id || null);
            }
        } catch (error) {
            console.error('Failed to load organization currency settings:', error);
        }
    }, [isAuthenticated, user]);

    // Load currencies and organization settings on authentication
    useEffect(() => {
        if (isAuthenticated) {
            loadCurrencies();
            loadOrganizationCurrency();
        } else {
            // Reset to defaults when logged out
            setCurrentCurrency(DEFAULT_CURRENCY);
            setCurrencyId(null);
            setExchangeRate(1);
        }
    }, [isAuthenticated, loadCurrencies, loadOrganizationCurrency]);

    /**
     * Change the current display currency
     */
    const changeCurrency = useCallback((currencyCode, newCurrencyId = null, newExchangeRate = 1) => {
        setCurrentCurrency(currencyCode);
        setCurrencyId(newCurrencyId);
        setExchangeRate(newExchangeRate);
        localStorage.setItem('preferredCurrency', currencyCode);
    }, []);

    /**
     * Reset to organization's default currency
     */
    const resetToOrganizationCurrency = useCallback(() => {
        setCurrentCurrency(organizationCurrency);
    }, [organizationCurrency]);

    /**
     * Format a value in the current currency
     */
    const formatAmount = useCallback((value, options = {}) => {
        if (!hasNumericValue(value)) return NULL_DISPLAY;
        return formatCurrencyUtil(value, currentCurrency, options);
    }, [currentCurrency, hasNumericValue]);

    /**
     * Format a base EUR amount explicitly as EUR.
     */
    const formatEurAmount = useCallback((value, options = {}) => {
        if (!hasNumericValue(value)) return NULL_DISPLAY;
        return formatCurrencyUtil(value, 'EUR', options);
    }, [hasNumericValue]);

    /**
     * Format a value in a specific currency (overriding current)
     */
    const formatAmountIn = useCallback((value, currencyCode, options = {}) => {
        if (!hasNumericValue(value)) return NULL_DISPLAY;
        return formatCurrencyUtil(value, currencyCode || currentCurrency, options);
    }, [currentCurrency, hasNumericValue]);

    /**
     * Format compact (for large numbers)
     */
    const formatCompact = useCallback((value) => {
        return formatCurrencyCompact(value, currentCurrency);
    }, [currentCurrency]);

    /**
     * Format a currency range
     */
    const formatRange = useCallback((minValue, maxValue) => {
        return formatCurrencyRange(minValue, maxValue, currentCurrency);
    }, [currentCurrency]);

    /**
     * Get the current currency symbol
     */
    const getSymbol = useCallback(() => {
        return getCurrencySymbol(currentCurrency);
    }, [currentCurrency]);

    /**
     * Get currency by ID from loaded currencies
     */
    const getCurrencyById = useCallback((id) => {
        return currencies.find(c => c.id === id);
    }, [currencies]);

    /**
     * Get currency by code from loaded currencies
     */
    const getCurrencyByCode = useCallback((code) => {
        return currencies.find(c => c.code === code || c.key === code);
    }, [currencies]);

    /**
     * Format from local/EUR pair:
     * - use local amount only if its currency matches current currency
     * - otherwise fallback to EUR base amount
     */
    const formatResolvedAmount = useCallback(({
        eurAmount,
        localAmount = null,
        localCurrencyId = null,
        localCurrencyCode = null,
        options = {},
    }) => {
        let resolvedLocalCode = localCurrencyCode;
        if (!resolvedLocalCode && localCurrencyId != null) {
            const c = currencies.find(x => x.id === localCurrencyId);
            resolvedLocalCode = c?.code || c?.key || null;
        }

        const normalizedCurrentCode = normalizeCurrencyCode(currentCurrency);
        const normalizedLocalCode = normalizeCurrencyCode(resolvedLocalCode);

        if (
            hasNumericValue(localAmount) &&
            normalizedLocalCode &&
            normalizedLocalCode === normalizedCurrentCode
        ) {
            return formatCurrencyUtil(localAmount, currentCurrency, options);
        }

        if (!hasNumericValue(eurAmount)) {
            return NULL_DISPLAY;
        }

        return formatCurrencyUtil(eurAmount, 'EUR', options);
    }, [currencies, currentCurrency, hasNumericValue, normalizeCurrencyCode]);

    /**
     * Convert EUR amount to current currency
     * (Uses stored exchange rate or defaults to 1)
     */
    const convertFromEur = useCallback((eurAmount) => {
        if (!eurAmount || currentCurrency === 'EUR') {
            return eurAmount;
        }
        return eurAmount * exchangeRate;
    }, [currentCurrency, exchangeRate]);

    /**
     * Get display values for a transaction/bill that has both EUR and local values.
     * No pseudo-conversion: fallback is always EUR base amount.
     */
    const getDisplayAmount = useCallback((eurAmount, localAmount, localCurrencyCode, localCurrencyId = null) => {
        let resolvedLocalCode = localCurrencyCode;
        if (!resolvedLocalCode && localCurrencyId !== null && localCurrencyId !== undefined) {
            const c = currencies.find(x => x.id === localCurrencyId);
            resolvedLocalCode = c?.code || c?.key || null;
        }

        const normalizedCurrentCode = normalizeCurrencyCode(currentCurrency);
        const normalizedLocalCode = normalizeCurrencyCode(resolvedLocalCode);

        // If we're viewing in the local currency and local amount is available
        if (normalizedLocalCode && normalizedLocalCode === normalizedCurrentCode && hasNumericValue(localAmount)) {
            return {
                amount: localAmount,
                currency: normalizedLocalCode,
                formatted: formatCurrencyUtil(localAmount, resolvedLocalCode),
            };
        }

        if (!hasNumericValue(eurAmount)) {
            return {
                amount: null,
                currency: 'EUR',
                formatted: NULL_DISPLAY,
                isConverted: false,
            };
        }

        return {
            amount: eurAmount,
            currency: 'EUR',
            formatted: formatCurrencyUtil(eurAmount, 'EUR'),
            isConverted: false,
        };
    }, [currencies, currentCurrency, hasNumericValue, normalizeCurrencyCode]);

    const value = {
        // State
        currentCurrency,
        currencyId,
        exchangeRate,
        currencies,
        currenciesLoading,
        organizationCurrency,

        // Actions
        changeCurrency,
        resetToOrganizationCurrency,
        loadCurrencies,

        // Formatting helpers
        formatAmount,
        formatEurAmount,
        formatAmountIn,
        formatCompact,
        formatRange,
        getSymbol,
        formatResolvedAmount,

        // Lookup helpers
        getCurrencyById,
        getCurrencyByCode,

        // Conversion helpers
        convertFromEur,
        getDisplayAmount,
    };

    return (
        <CurrencyContext.Provider value={value}>
            {children}
        </CurrencyContext.Provider>
    );
};

export default CurrencyProvider;
